import { select } from "redux-saga/effects";
import { IAction } from "reduxsauce";
import { IUnitsDataBase, IUser, IUsersAnalytics, UserStatus } from "../App/Analytics/Common";
import { ICourseComponentPart } from "../App/Analytics/Common";
import { lastSeenValue, timeTakenValue } from "../formatters/users";
import * as selectors from "../selectors/selectors";
import { exportDataAsCSV, exportDataAsXLSX, Table } from "./export-data";
import { parseAssessmentContent, htmlToPlainText } from "./helpers/exports";

type CommonHeaders = "Organisation Title" | "Master Course Title" | "SCORM Export Title";

type UserHeaders = "Name" | "State" | "Last Seen" | "Resets" | "Time Taken" | "Teachings" | "Package State";

type AttemptsHeaders =
  | "Unit Name"
  | "Question Name"
  | "Question Type"
  | "Option Name"
  | "Correct Option"
  | "Nº Correct Answers"
  | "Nº Incorrect Answers"
  | "Nº Didn't Know Answers"
  | "% Correct Answers"
  | "% Incorrect Answers"
  | "% Didn't Know Answers"
  | "Total responses";

type ColumnHeader = CommonHeaders | UserHeaders;

type AttemptsAnalyticsReportHeader = CommonHeaders | AttemptsHeaders;

type Row = [string, string, string, string, string, string, number, string, number, string];

type AttemptsRow = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  number,
  number,
  number,
  string,
  string,
  string,
  number
];

function stateValue(u: IUser, ua: IUsersAnalytics): string {
  if (ua[u.accountId] === undefined) {
    return UserStatus.NotStarted;
  } else if (ua[u.accountId].completed) {
    return UserStatus.Completed;
  } else if (ua[u.accountId].lastSeen !== null) {
    return UserStatus.InProgress;
  } else {
    return UserStatus.NotStarted;
  }
}

interface ReportDataActionBase extends IAction {
  objectDisplayName: string;
  organizationName: string;
}

export interface IUserReportDataAction extends ReportDataActionBase {
  courseDisplayName: string;
  users: IUser[];
  usersAnalytics: IUsersAnalytics;
  packageState: string;
  isXLSX?: boolean;
}

export interface IAttemptsAnalyticsReportDataAction extends ReportDataActionBase {
  content: ICourseComponentPart;
  courseDisplayName: string;
  selectedUnitName: string;
  attemptsAnalytics: Record<string, IUnitsDataBase> | undefined;
}

const resultHeader: ColumnHeader[] = [
  "Organisation Title",
  "Master Course Title",
  "SCORM Export Title",
  "Name",
  "State",
  "Last Seen",
  "Resets",
  "Time Taken",
  "Teachings",
  "Package State",
];

const attemptsAnalyticsResultHeader: AttemptsAnalyticsReportHeader[] = [
  "Organisation Title",
  "Master Course Title",
  "SCORM Export Title",
  "Unit Name",
  "Question Name",
  "Question Type",
  "Option Name",
  "Correct Option",
  "Nº Correct Answers",
  "Nº Incorrect Answers",
  "Nº Didn't Know Answers",
  "% Correct Answers",
  "% Incorrect Answers",
  "% Didn't Know Answers",
  "Total responses",
];

export function generateUsersReportData(action: IUserReportDataAction): Table<ColumnHeader, Row> {
  const tableHeader: Table<ColumnHeader, Row> = [resultHeader];
  const resultData = generateRows(action);
  return tableHeader.concat(resultData);
}

export function generateAttemptsReportData(
  action: IAttemptsAnalyticsReportDataAction
): Table<AttemptsAnalyticsReportHeader, AttemptsRow> {
  const tableHeader: Table<AttemptsAnalyticsReportHeader, AttemptsRow> = [attemptsAnalyticsResultHeader];
  const resultData = generateAttemptRows(action);
  return tableHeader.concat(resultData);
}

export function generateRows(action: IUserReportDataAction): Row[] {
  const ua = action.usersAnalytics;
  return action.users.map(u => [
    action.organizationName,
    action.courseDisplayName,
    action.objectDisplayName,
    u.displayName,
    stateValue(u, ua),
    lastSeenValue(ua[u.accountId]),
    ua[u.accountId] === undefined ? 0 : ua[u.accountId].resets || 0,
    timeTakenValue(ua[u.accountId]),
    ua[u.accountId] === undefined ? 0 : ua[u.accountId].teachings || 0,
    action.packageState,
  ]);
}
export function generateAttemptRows(action: IAttemptsAnalyticsReportDataAction): AttemptsRow[] {
  const rows: AttemptsRow[] = [];

  if (!action.attemptsAnalytics) {
    throw new Error("Error processing analytics data");
  }
  const unitContent = parseAssessmentContent(action.content, action.masterLocale);
  const analytics = action.attemptsAnalytics;

  const emptyAnalytics = {
    Correct: {
      amount: 0,
      percentage: 0,
    },
    "Didn't Know": {
      amount: 0,
      percentage: 0,
    },
    Incorrect: {
      amount: 0,
      percentage: 0,
    },
  };

  const firstValidChoiceKey = Object.keys(analytics)[0];
  const totals = firstValidChoiceKey ? analytics[firstValidChoiceKey].totals : 0;

  unitContent.choices.forEach(choice => {
    if (!choice.deleted) {
      const choiceData = analytics[choice.id] ?? emptyAnalytics;
      const elementByLocale = choice.text.elems.find(e => e.locale === action.masterLocale) || choice.text.elems[0];
      const choiceName = htmlToPlainText(elementByLocale.value?.text);
      rows.push([
        action.organizationName,
        action.courseDisplayName,
        action.objectDisplayName,
        action.selectedUnitName,
        unitContent.questionName,
        unitContent.questionType,
        choiceName,
        choice.correct ? "Correct" : "Incorrect",
        choiceData.Correct.amount,
        choiceData.Incorrect.amount,
        choiceData["Didn't Know"].amount,
        `${choiceData.Correct.percentage}%`,
        `${choiceData.Incorrect.percentage}%`,
        `${choiceData["Didn't Know"].percentage}%`,
        totals,
      ]);
    }
  });

  return rows;
}

export function* exportAllUsersData(rows: Table<ColumnHeader, Row>) {
  const tableHeader: Table<ColumnHeader, Row> = [resultHeader];
  yield exportDataAsCSV(tableHeader.concat(rows));
}

export function* exportUsers(action: IUserReportDataAction) {
  const organizationName: string = yield select(selectors.organizationName);
  const resultData = generateUsersReportData({ ...action, organizationName });
  action.isXLSX ? yield exportDataAsXLSX(resultData, "export") : yield exportDataAsCSV(resultData);
}

export function* exportAttemptsAnalytics(action: IAttemptsAnalyticsReportDataAction) {
  const organizationName: string = yield select(selectors.organizationName);
  const resultData = generateAttemptsReportData({ ...action, organizationName });
  yield exportDataAsXLSX(resultData, "export");
}
